import { useState } from "react";
import { useSelector } from "react-redux";
import { Box, Button, Chip, Paper, TextField, Typography } from "@mui/material";
import GavelIcon from "@mui/icons-material/Gavel";
import { addNegotiationProposal, respondToProposal } from "../../api/caseNegotiations";
import useErrorHandler from "../../hooks/useErrorHandler";
import useSnackbar from "../../hooks/useSnackbar";
import { fmtDateTime, toUSD } from "./negotiation-ui";
import TermsEditor, { TermsList, readyItems, useTermCatalog } from "./TermsEditor";

const STATUS_CHIP = {
  PENDING: "!bg-[#fff4e0] !text-[#b26a00] !border-[#b26a00]",
  ACCEPTED: "!bg-[#e7f6ee] !text-[#1b6e47] !border-[#1b6e47]",
  REJECTED: "!bg-[#fdecea] !text-[#c62828] !border-[#c62828]",
};

function answerText(r) {
  return r === "ACCEPTED" ? "accepted" : r === "REJECTED" ? "declined" : "not answered yet";
}

function PendingProposal({ p, viewer, onRespond, busy }) {
  const isParty = Boolean(viewer?.side);
  const answered = Boolean(p.myResponse);
  return (
    <Box className="rounded-lg border border-solid border-[#e6c27a] bg-[#fffaf0] p-3">
      <Box className="flex flex-wrap items-start justify-between gap-2">
        <Box>
          <Typography fontSize="small" color="text.secondary">
            Proposed by {p.proposed_by_name} on {fmtDateTime(p.created_at)}
          </Typography>
          {p.monetary !== false ? (
            <Typography variant="h5" className="!font-semibold">
              {toUSD(p.amount)}
            </Typography>
          ) : (
            <Typography fontWeight={600}>Terms only</Typography>
          )}
        </Box>
        <Chip size="small" variant="outlined" label="Awaiting both parties" className={`${STATUS_CHIP.PENDING} !font-semibold`} />
      </Box>
      {p.text ? <Typography className="mt-1 whitespace-pre-wrap">{p.text}</Typography> : null}
      <TermsList items={p.items} />
      {p.terms ? (
        <Typography fontSize="small" className="mt-1 whitespace-pre-wrap">
          <b>Other terms:</b> {p.terms}
        </Typography>
      ) : null}

      {isParty ? (
        <Box className="mt-3">
          <Box className="flex flex-wrap items-center gap-2">
            <Button variant="contained" color="success" disabled={answered || busy} onClick={() => onRespond(p.id, "ACCEPT")} className="normal-case">
              Accept proposal
            </Button>
            <Button variant="outlined" color="error" disabled={answered || busy} onClick={() => onRespond(p.id, "REJECT")} className="normal-case">
              Decline
            </Button>
          </Box>
          <Typography fontSize="small" color="text.secondary" className="mt-2">
            {answered
              ? `You ${answerText(p.myResponse)} this proposal. ${
                  p.otherResponded ? "The other side has also answered." : "Waiting for the other side to answer."
                } The proposal settles the negotiation only if both sides accept.`
              : p.otherResponded
              ? "The other side has answered. Your answer decides the outcome."
              : "The other side has not answered yet. The proposal settles the negotiation only if both sides accept."}
          </Typography>
        </Box>
      ) : (
        <Typography fontSize="small" color="text.secondary" className="mt-2">
          Plaintiff: {answerText(p.plaintiffResponse)}. Defendant: {answerText(p.defendantResponse)}.
        </Typography>
      )}
    </Box>
  );
}

export default function ProposalPanel({ caseId, negId, viewer, proposals, monetary = true, onChanged }) {
  const { data: user } = useSelector((state) => state.user);
  const snackbar = useSnackbar();
  const errorHandler = useErrorHandler();
  const { catalog, maxItems } = useTermCatalog(caseId);
  const [termItems, setTermItems] = useState([]);
  const [form, setForm] = useState({ amount: "", text: "", terms: "" });
  const [amountError, setAmountError] = useState("");
  const [busy, setBusy] = useState(false);

  const pending = proposals?.pending || null;
  const history = proposals?.history || [];
  if (!viewer?.canPropose && !pending && !history.length) return null;

  function onChange({ target }) {
    setForm((prev) => ({ ...prev, [target.name]: target.value }));
    if (target.name === "amount") setAmountError("");
  }

  async function onPropose(e) {
    e.preventDefault();
    const amount = Number(form.amount);
    if (monetary && !(amount > 0)) {
      setAmountError("Enter an amount greater than $0.");
      return;
    }
    const ready = readyItems(termItems, catalog);
    if (ready.error) {
      snackbar.showError(ready.error);
      return;
    }
    if (!monetary && !ready.items.length) {
      snackbar.showError("Add at least one term.");
      return;
    }
    setBusy(true);
    try {
      await addNegotiationProposal(user.token, caseId, negId, {
        ...(monetary ? { amount } : {}),
        ...(ready.items.length ? { terms_items: ready.items } : {}),
        ...(form.text.trim() ? { text: form.text.trim() } : {}),
        ...(form.terms.trim() ? { terms: form.terms.trim() } : {}),
      });
      setForm({ amount: "", text: "", terms: "" });
      setTermItems([]);
      snackbar.showSuccess("Settlement proposal sent to both parties");
    } catch (error) {
      errorHandler(error);
    } finally {
      setBusy(false);
      onChanged();
    }
  }

  async function onRespond(proposalId, response) {
    setBusy(true);
    try {
      const { data } = await respondToProposal(user.token, caseId, negId, proposalId, response);
      const outcome = data?.outcome;
      snackbar.showSuccess(
        outcome === "SETTLED"
          ? "Both sides accepted. The negotiation is settled."
          : outcome === "REJECTED"
          ? "The proposal was declined."
          : "Your answer was recorded."
      );
    } catch (error) {
      errorHandler(error);
    } finally {
      setBusy(false);
      onChanged();
    }
  }

  return (
    <Paper variant="outlined" className="mt-4 rounded-xl p-4">
      <Box className="flex items-center gap-2">
        <GavelIcon fontSize="small" className="text-brand" />
        <Typography fontWeight={600}>Arbitrator&apos;s settlement proposal</Typography>
      </Box>

      {pending ? (
        <Box className="mt-3">
          <PendingProposal p={pending} viewer={viewer} onRespond={onRespond} busy={busy} />
        </Box>
      ) : null}

      {viewer?.canPropose ? (
        <Box component="form" onSubmit={onPropose} noValidate className="mt-3 flex flex-col gap-2">
          <Typography fontSize="small" color="text.secondary">
            {pending
              ? "Sending a new proposal replaces the pending one."
              : "Propose a settlement. It settles the negotiation only if both parties accept it."}
          </Typography>
          <Box className="flex flex-wrap gap-2">
            {monetary ? (
              <TextField
                type="number"
                size="small"
                label="Proposed amount ($)"
                name="amount"
                value={form.amount}
                onChange={onChange}
                inputProps={{ min: 0, step: "any" }}
                error={Boolean(amountError)}
                helperText={amountError}
                className="w-[230px]"
              />
            ) : null}
            <TextField
              size="small"
              label="Reasoning (optional)"
              name="text"
              value={form.text}
              onChange={onChange}
              inputProps={{ maxLength: 2000 }}
              className="flex-1 min-w-[220px]"
            />
          </Box>
          <Typography fontSize="small" fontWeight={600}>
            Terms{monetary ? " (optional)" : ""}
          </Typography>
          <TermsEditor catalog={catalog} maxItems={maxItems} items={termItems} onChange={setTermItems} />
          <TextField
            multiline
            minRows={2}
            size="small"
            label="Additional terms (optional)"
            name="terms"
            value={form.terms}
            onChange={onChange}
            inputProps={{ maxLength: 2000 }}
          />
          <Box>
            <Button type="submit" variant="contained" disabled={busy} className="normal-case">
              Send proposal
            </Button>
          </Box>
        </Box>
      ) : null}

      {history.length ? (
        <Box className="mt-4">
          <Typography fontSize="small" fontWeight={600} color="text.secondary" className="!mb-1">
            Earlier proposals
          </Typography>
          {history.map((h) => (
            <Box key={h.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 border-0 border-t border-solid border-[#eef1f5] py-2">
              <Typography fontWeight={600} className="w-[110px]">
                {h.monetary === false ? "Terms only" : toUSD(h.amount)}
              </Typography>
              <Chip size="small" variant="outlined" label={h.status} className={`${STATUS_CHIP[h.status] || ""} !font-semibold`} />
              <Typography fontSize="small" color="text.secondary">
                {h.proposed_by_name} · {fmtDateTime(h.created_at)}
              </Typography>
              {h.items?.length ? (
                <Box className="basis-full">
                  <TermsList items={h.items} dense />
                </Box>
              ) : null}
              {h.text || h.terms ? (
                <Typography fontSize="small" className="basis-full">
                  {[h.text, h.terms ? `Terms: ${h.terms}` : null].filter(Boolean).join(" · ")}
                </Typography>
              ) : null}
            </Box>
          ))}
        </Box>
      ) : null}
    </Paper>
  );
}
